import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, debounceTime, tap } from 'rxjs/operators';
import { Application, Unit } from '../../models/admin-models';
import { AdminService } from '../admin.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { saveAs } from '@progress/kendo-file-saver';
import { UnitProfileUploadComponent } from '../unit-profile-upload/unit-profile-upload.component';
import { UnitHistoryDownloadModalComponent } from '../../reporting/reports/unit-profile-history/unit-history-download-modal/unit-history-download-modal.component';
import { WarnDialogComponent } from '../../warn-dialog/warn-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { HasPermissionDirective } from '../../directives/permissions.directive';

const appList = [
  { value: 'aw', ui: 'aw', name: 'Annuity Wizard' },
  { value: 'iw', ui: 'iw', name: 'Investment Wizard' },
  { value: 'li', ui: 'li', name: 'Life Insurance Wizard' },
  { value: 'pp', ui: 'pp', name: 'Product Profiler' },
  { value: 'rb', ui: 'rb', name: 'Control KB (RB)' },
  { value: 'cm', ui: 'cm', name: 'Case Management (CM)' },
  { value: 'pm', ui: 'pm', name: 'Book Management (PM)' },
  { value: 'shared', ui: 'rb-unit-shared', name: 'Shared' },
  { value: 'dl', ui: 'tm-unit', name: 'Transaction Monitor' },
  { value: 'arl', ui: 'am-unit', name: 'Account Monitor' },
  { value: 'fpml', ui: 'fpm-unit', name: 'FP Monitor' },
  { value: 'amlal', ui: 'aml-unit', name: 'AML Monitor' },
];

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    HasPermissionDirective,
  ],
})
export class ToolbarComponent implements OnInit {
  private admnSrvc = inject(AdminService);
  private dialog = inject(MatDialog);
  private ss = inject(SessionStorageService);

  @Output() unitAppSelected = new EventEmitter();
  @Output() unitAppReport = new EventEmitter();
  @Input() hideReportType = false;
  @Input() profileFormat = false;
  @Input() ignoreUnitValidation = false;
  @Input() showAllApps = false;

  unitsControl = new FormControl();
  units: Unit[] = [] as Unit[];
  reportTypes: string[];
  applications: Application[] = [];
  selectedApp;
  selectedUnit;
  selectedType;
  filteredUnits;
  uiType;
  canSearch = false;

  ngOnInit() {
    if (this.showAllApps) {
      this.applications = appList;
    } else {
      this.ss.get('globals')?.Modules.forEach(el => {
        const availableApp = appList.find(
          app => app.value === el.toLowerCase()
        );

        if (availableApp) {
          this.applications.push(availableApp);
        }
      });
    }
    this.getUnits();
    this.reportTypes = [];
  }

  getUnits() {
    this.unitsControl.valueChanges
      .pipe(
        tap(() => {
          this.canSearch = false;
        }),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (value) {
          this.admnSrvc.getUnitList(value, true).subscribe(resp => {
            if (resp['names']) {
              this.filteredUnits = resp['names'];
              this.canSearch = true;
            }
          });
        }
      });
  }

  checkSelection(btnCheck = false) {
    if (!this.ignoreUnitValidation) {
      if (
        !this.filteredUnits ||
        !this.filteredUnits.find(unit => {
          return (
            unit.id.toLowerCase() === this.unitsControl.value.toLowerCase()
          );
        })
      ) {
        if (btnCheck) {
          return false;
        }
        this.unitsControl.setValue(null);
      }
    }
    return true;
  }

  autoCompleteUnitChanged(ev) {
    if (this.unitsControl.value !== ev.option.value) {
      this.unitsControl.setValue(ev.option.value);
    } else {
      this.canSearch = true;
    }
  }

  onAppChange() {
    const app = ['shared', 'tm', 'am', 'fpm'].includes(this.selectedApp)
      ? 'rb'
      : this.selectedApp;
    this.admnSrvc.getReportTypes(app).subscribe(data => {
      this.reportTypes = data['reportTypes'];
      this.selectedType = this.reportTypes[0];
    });
  }

  viewUnitProfile() {
    const uiConfig = ['shared', 'tm', 'am', 'fpm'].includes(this.selectedApp)
      ? this.applications.find(x => x.value === this.selectedApp).ui
      : null;
    const app = ['shared'].includes(this.selectedApp) ? 'rb' : this.selectedApp;
    this.unitAppSelected.emit({
      app: app,
      unit: this.selectedUnit,
      uiType: uiConfig,
    });
  }

  viewUnitProfileReport() {
    const app = ['shared', 'tm', 'am', 'fpm'].includes(this.selectedApp)
      ? 'rb'
      : this.selectedApp;
    this.unitAppReport.emit({
      app: app,
      unit: this.selectedUnit,
      type: this.selectedType,
    });
  }

  uploadUnitProfile() {
    let dialogRef = this.dialog.open(UnitProfileUploadComponent);
    dialogRef.afterClosed().subscribe(() => {
      dialogRef = null;
    });
  }

  downloadUnitProfile() {
    let dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Disclosure',
        aiDisclosure: true,
        confirm: 'Agree and Download',
        cancel: 'Cancel/Exit',
        hideCancel: false,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        let app;

        switch (this.selectedApp) {
          case 'shared':
            app = 'rb';
            break;
          case 'tm':
            app = 'dl';
            break;
          case 'am':
            app = 'arl';
            break;
          case 'fpm':
            app = 'fpml';
            break;
          case 'amlal':
            app = 'amlal';
            break;
          default:
            app = this.selectedApp;
        }

        this.admnSrvc
          .downloadUnitProfile(app, this.selectedUnit)
          .subscribe(data => {
            const date = new Date();

            let fileData;
            const filename = `${app}-${this.selectedUnit}-unitProfile-${
              date.getMonth() + 1
            }${date.getDate()}${date.getFullYear()}.xml`;
            const reader = new FileReader();
            reader.readAsDataURL(data);

            reader.onloadend = () => {
              fileData = reader.result;
              saveAs(fileData, filename);
            };
          });
      }
      dialogRef = null;
    });
  }

  downloadUnitProfileHistoryReport() {
    let dialogWarnRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Disclosure',
        aiDisclosure: true,
        confirm: 'Agree and Download',
        cancel: 'Cancel/Exit',
        hideCancel: false,
      },
    });
    dialogWarnRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        let app;
        switch (this.selectedApp) {
          case 'dl':
            app = 'dlfe';
            break;
          case 'arl':
            app = 'arlfe';
            break;
          case 'fpml':
            app = 'fpmlfe';
            break;
          case 'amlal':
            app = 'amlalfe';
            break;
          case 'shared':
            app = 'rb';
            break;
          default:
            app = this.selectedApp;
            break;
        }
        let dialogRef = this.dialog.open(UnitHistoryDownloadModalComponent, {
          data: {
            unit: this.selectedUnit,
            app: app,
          },
        });
        dialogRef.afterClosed().subscribe(() => {
          dialogRef = null;
        });
      }
      dialogWarnRef = null;
    });
  }
}
